/**
 * A withdrawal waiting for its markets, as arithmetic — `docs/06` §F126.
 *
 * Money a user withdraws leaves the trading balance at once and is set aside.
 * USDU records `readyAt` — the end of the last market the money could be in,
 * the market running thirty seconds after the request — and `finishUnwrap`
 * pays it once the exchange's `settledThrough()` has reached `readyAt`: every
 * market ending by then has settled. Past `readyAt + WITHDRAWAL_BACKSTOP` (48
 * hours) it pays regardless, so nothing the venue does can hold the money for
 * good. `cancelUnwrap` puts it back at any time before.
 *
 * **What is in no market does not wait.** Through the app the venue pays that
 * part in the same transaction (`RelayReceipt.paid_now`), and a withdrawal
 * asked for from the user's own wallet has it paid by the venue's relayer
 * within seconds. What is set aside is only what an open trade or a resting
 * order could still take. Until §F126 every withdrawal waited a fixed
 * `UNWRAP_DELAY` of two minutes from the request, and nothing here may promise
 * that number again.
 *
 * `wallet/usdu.ts` is the chain half; this is the half a screen needs to render
 * it, and it lives here rather than there because `wallet/usdu.ts` is reachable
 * only through `<WalletProvider>`'s lazy boundary (invariant 13), while a panel
 * imports this statically.
 *
 * `domain/`, so no React (invariant 10); every instant is an integer `_ms`, and
 * the one float that arrives — the corrected clock — is floored where it
 * crosses (invariant 7).
 */

import { parseFixed, USDC_DP } from '@/lib/decimal'
import { formatUtcTime } from '@/lib/format'

/**
 * An unwrap that has been asked for and not yet paid out.
 *
 * `amount` is micro-USDU and has already left the trading balance. `readyAtMs`
 * is the chain's `readyAt` — whole seconds — times a thousand: the end of the
 * last market the money could be in. `amount === 0n` is nothing pending, which
 * is a read, and distinct from `null` in `WrapperState`, which is not.
 */
export interface PendingUnwrap {
  amount: bigint
  readyAtMs: number
}

/**
 * What the token says about settlement, read beside the withdrawal. Both are
 * the contract's numbers, never written here, and `null` is unread.
 *
 * `settledThroughMs` is `USDU.settledThrough()` times a thousand — the instant
 * up to which every market has settled (zero when the exchange cannot answer,
 * which leaves every withdrawal to the backstop). `backstopMs` is
 * `WITHDRAWAL_BACKSTOP()` times a thousand.
 */
export interface SettlementProgress {
  settledThroughMs: number | null
  backstopMs: number | null
}

/**
 * How long after its markets end a withdrawal is usually released: the print
 * at the boundary lands within a few seconds, the market settles on chain, and
 * the venue pays out. An estimate for the "about" time on screen, and never a
 * promise — the token waits for the settlement itself, however long it takes.
 */
export const RELEASE_ESTIMATE_MS = 10_000

/** Past `readyAt` by this much and still not settled is "taking longer than usual". */
export const LATE_AFTER_MS = 60_000

export type UnwrapPhase =
  | { kind: 'NONE' }
  | { kind: 'UNREAD' }
  /** Its markets are still trading. */
  | { kind: 'WAITING'; amount: bigint; readyAtMs: number; expectedAtMs: number }
  /** Its markets have ended and settlement has not reached them yet — ordinarily seconds. */
  | { kind: 'SETTLING'; amount: bigint; readyAtMs: number; expectedAtMs: number }
  /**
   * Still not settled a minute after its markets ended. `backstopAtMs` is the
   * instant past which the token pays regardless, `null` when it was not read.
   */
  | { kind: 'LATE'; amount: bigint; readyAtMs: number; backstopAtMs: number | null }
  /** `finishUnwrap` would pay it now. */
  | { kind: 'READY'; amount: bigint }

/**
 * Where a withdrawal stands at `nowMs` — a server-corrected instant from
 * `lib/clock`, never `Date.now()` (invariant 7).
 *
 * **`READY` is exactly when `finishUnwrap` would pay**, because it gates a
 * button and a button that reverts is a wallet confirmation that fails:
 *
 * - settlement has reached `readyAt` **and** the clock has too — the token
 *   refuses `UnwrapNotReady` before `readyAt` whatever settlement says; or
 * - the clock is past `readyAt + backstop` — **strictly**, in whole chain
 *   seconds, so the first block that pays without settlement is stamped a
 *   second later, and the button is offered then and not at it.
 *
 * A `settledThroughMs` read before `readyAt` says nothing about after it, so
 * the screen re-reads it (`settlementRecheckAtMs`); until then the phase moves on
 * by the clock alone.
 */
export function unwrapPhase(pending: PendingUnwrap | null, progress: SettlementProgress, nowMs: number): UnwrapPhase {
  if (pending === null) return { kind: 'UNREAD' }
  if (pending.amount === 0n) return { kind: 'NONE' }
  const { amount, readyAtMs } = pending
  const now = Math.floor(nowMs)
  const settled = progress.settledThroughMs !== null && progress.settledThroughMs >= readyAtMs
  const backstopAtMs = progress.backstopMs === null ? null : readyAtMs + progress.backstopMs
  if (settled && now >= readyAtMs) return { kind: 'READY', amount }
  if (backstopAtMs !== null && now >= backstopAtMs + 1000) return { kind: 'READY', amount }
  const expectedAtMs = readyAtMs + RELEASE_ESTIMATE_MS
  if (now < readyAtMs) return { kind: 'WAITING', amount, readyAtMs, expectedAtMs }
  if (now < readyAtMs + LATE_AFTER_MS) return { kind: 'SETTLING', amount, readyAtMs, expectedAtMs }
  return { kind: 'LATE', amount, readyAtMs, backstopAtMs }
}

/**
 * When, after `readyAt`, a withdrawal still waiting on settlement re-reads it.
 * Bounded: six looks in five minutes, then none — a page opened later reads on
 * mount, and a payout the venue makes is pushed (`withdrawalReadBlock`).
 */
export const SETTLEMENT_RECHECK_AFTER_MS: readonly number[] = [5_000, 15_000, 30_000, 60_000, 120_000, 300_000]

/**
 * The next instant to re-read `settledThrough()` at — or `null`, which is
 * almost always the answer.
 *
 * **Why there is a look at all.** The token pays once settlement has reached
 * the withdrawal's markets, and settlement is something the venue does, a few
 * seconds after those markets end: a `settledThroughMs` read while they were
 * trading cannot say when it has. Ordinarily the venue pays out the moment it
 * may and pushes the payout, and the row goes; these looks are for when it has
 * not — a venue with no relayer, where the user finishes it, or a relayer that
 * has stopped — so that **Finish** is offered once it would work rather than
 * never.
 *
 * Only while the clock is past `readyAt` (before it nothing can have settled)
 * and the read says unsettled, and only instants after `sinceMs` — the last
 * read this screen made, or when it mounted. A page opened long after
 * `readyAt` has just read, and looking again at once would only repeat it.
 *
 * `sinceMs` and not the clock decides which look is next, so the answer holds
 * still while the clock ticks past it: a screen that re-renders every second
 * must not drop a look it was about to make because a tick got there first.
 */
export function settlementRecheckAtMs(
  pending: PendingUnwrap | null,
  progress: SettlementProgress,
  nowMs: number,
  sinceMs: number,
): number | null {
  const phase = unwrapPhase(pending, progress, nowMs)
  if (phase.kind !== 'SETTLING' && phase.kind !== 'LATE') return null
  for (const offset of SETTLEMENT_RECHECK_AFTER_MS) {
    const atMs = phase.readyAtMs + offset
    if (atMs > sinceMs) return atMs
  }
  return null
}

/**
 * The block to re-read the withdrawal at, because the venue pushed a change to
 * it — or `null`, which is almost always the answer and costs nothing.
 *
 * **The venue says when; the wallet is asked once.** The relayer pays a
 * withdrawal out with no action from this tab, and the venue's chain watcher
 * sees the payout and pushes the account's withdrawal on the `balance` channel
 * with the block it saw it in. Re-reading on a timer did not work: MetaMask
 * answers `latest` from a block it cached, so every look found the withdrawal
 * still pending (2026-10-07), and every look was requests from every user's
 * wallet. A read pinned to the pushed block is answered fresh.
 *
 * Two conditions, both needed:
 *
 * - **The push disagrees with what is on screen.** A balance frame arrives on
 *   every fill and settlement too; those re-read nothing.
 * - **The push is newer than the read.** The screen's own actions re-read at
 *   their receipt's block, usually before the venue's frame for that block
 *   arrives, and an older frame arriving after must not wind the screen back.
 *   A read at `latest` has no block (`readAtBlock === null`) and any push wins.
 *
 * `read === null` is unread, which this leaves to the read already under way.
 */
export function withdrawalReadBlock(
  pushed: { pending: PendingUnwrap; atBlock: bigint } | null,
  read: PendingUnwrap | null,
  readAtBlock: bigint | null,
): bigint | null {
  if (pushed === null || read === null) return null
  if (readAtBlock !== null && pushed.atBlock <= readAtBlock) return null
  const same = pushed.pending.amount === read.amount && (read.amount === 0n || pushed.pending.readyAtMs === read.readyAtMs)
  return same ? null : pushed.atBlock
}

/**
 * What a withdrawal sent through the venue did at once — `openapi.RelayReceipt`.
 *
 * `paidNow` reached the wallet in the transaction (it is read once that has
 * mined); the rest of `amount` is set aside until `readyAtMs`'s markets have
 * settled. `readyAtMs` is `null` when the whole amount was paid at once.
 */
export interface WithdrawalReceipt {
  amount: bigint
  paidNow: bigint
  readyAtMs: number | null
}

/**
 * The 202's body, narrowed. A `paid_now` that is absent or `null` is nothing
 * paid at once — a venue from before §F126 set the whole amount aside — and
 * the row for what is set aside, read from the chain, says the rest. A body
 * that contradicts itself is `null` rather than a sentence about money that
 * did not move.
 */
export function withdrawalReceiptOf(body: {
  amount: string
  paid_now?: string | null
  ready_at_ms: number | null
}): WithdrawalReceipt | null {
  try {
    const amount = parseFixed(body.amount, USDC_DP)
    const paidNow = typeof body.paid_now === 'string' ? parseFixed(body.paid_now, USDC_DP) : 0n
    if (paidNow < 0n || paidNow > amount) return null
    return { amount, paidNow, readyAtMs: typeof body.ready_at_ms === 'number' ? Math.floor(body.ready_at_ms) : null }
  } catch {
    return null
  }
}

/**
 * How fast a withdrawal arrives, in one sentence, for the screens that add
 * funds — said before the money goes in, and without a number: the wait is
 * however long the markets the money is in take to settle.
 *
 * With a relayer the part in no market is paid at once; without one, nothing
 * is, and the whole amount waits for the markets running when it was asked.
 */
export function withdrawalSpeedSentence(relayEnabled: boolean): string {
  return relayEnabled ?
      'Withdrawals reach your wallet in seconds; money in an open trade or order follows once its market settles.'
    : 'A withdrawal reaches your wallet once the markets running when you ask have settled.'
}

/** `21:47` — the minute a withdrawal's markets end, in UTC like every market label. */
export function marketsEndLabel(readyAtMs: number): string {
  return formatUtcTime(readyAtMs).slice(0, 5)
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const

/**
 * `20 Oct 21:47 UTC` — the backstop, two days out, so always with its date.
 * `SessionKeyList`'s format, for the same reason: a time alone two days ahead
 * names the wrong day.
 */
export function formatUtcDayTime(atMs: number): string {
  const day = new Date(Math.floor(atMs))
  return `${day.getUTCDate()} ${MONTHS[day.getUTCMonth()]} ${formatUtcTime(atMs).slice(0, 5)} UTC`
}
